import type { TimeReportDto } from "model/TimeReport";
import type { Setting } from "model/Workgroup";
import { isSettingEnabled } from "model/settings.logic";
import {
  isReportedTimeWindowValid,
  mergeIsoDateWithTimeInput,
  minutesBetweenIso,
  normalizeTimeReportTimesOvernight,
  signedMinutesBetweenIso,
  toTimeInputValue,
} from "web-apps/company/utils/timeAndDate";

const COMPANY_TIME_REPORT_STAMP_BEFORE_START_THRESHOLD =
  "CompanyTimeReportStampBeforeStartThreshold";
const COMPANY_TIME_REPORT_STAMP_AFTER_START_THRESHOLD =
  "CompanyTimeReportStampAfterStartThreshold";
const COMPANY_TIME_REPORT_STAMP_BEFORE_END_THRESHOLD =
  "CompanyTimeReportStampBeforeEndThreshold";
const COMPANY_TIME_REPORT_STAMP_AFTER_END_THRESHOLD =
  "CompanyTimeReportStampAfterEndThreshold";
const COMPANY_STAMP_CLOCK_ALLOW_EDIT_TIME = "CompanyStampClockAllowEditTime";

export interface ClockStampThresholds {
  beforeStartMinutes: number;
  afterStartMinutes: number;
  beforeEndMinutes: number;
  afterEndMinutes: number;
}

const resolveCompanySettingMinutes = (
  companySettings: Setting[] | undefined,
  key: string,
): number => {
  const raw =
    companySettings?.find((setting) => setting.key === key)?.value ?? "0";
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
};

/** Resolves clock-stamp before/after thresholds from company settings with safe defaults. */
export const resolveClockStampThresholds = (
  companySettings: Setting[] | undefined,
): ClockStampThresholds => ({
  beforeStartMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_BEFORE_START_THRESHOLD,
  ),
  afterStartMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_AFTER_START_THRESHOLD,
  ),
  beforeEndMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_BEFORE_END_THRESHOLD,
  ),
  afterEndMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_AFTER_END_THRESHOLD,
  ),
});

/** Whether the worker can edit reported and break times when stamping. */
export const resolveCanEditTime = (
  companySettings: Setting[] | undefined,
): boolean => {
  return isSettingEnabled(companySettings, COMPANY_STAMP_CLOCK_ALLOW_EDIT_TIME);
};

export interface NormalizedStampReportTimes {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

export interface StampReportTimeInputs {
  reportedStartBaseIso: string;
  reportedEndBaseIso: string;
  breakStartBaseIso: string;
  breakEndBaseIso: string;
  reportedStart: string;
  reportedEnd: string;
  breakStart: string;
  breakEnd: string;
}

/**
 * Merges reported and break HH:mm inputs onto the report's base ISO dates and
 * normalizes for overnight shifts. Returns null when any field is missing or
 * unparseable, so the same overnight-corrected times back both validation and submit.
 */
export const resolveStampReportTimes = (
  inputs: StampReportTimeInputs,
): NormalizedStampReportTimes | null => {
  const startTime = mergeIsoDateWithTimeInput(
    inputs.reportedStartBaseIso,
    inputs.reportedStart,
  );
  const endTime = mergeIsoDateWithTimeInput(
    inputs.reportedEndBaseIso,
    inputs.reportedEnd,
  );
  const breakStartTime = mergeIsoDateWithTimeInput(
    inputs.breakStartBaseIso,
    inputs.breakStart,
  );
  const breakEndTime = mergeIsoDateWithTimeInput(
    inputs.breakEndBaseIso,
    inputs.breakEnd,
  );
  if (!startTime || !endTime || !breakStartTime || !breakEndTime) {
    return null;
  }
  return normalizeTimeReportTimesOvernight({
    startTime,
    endTime,
    breakStartTime,
    breakEndTime,
  });
};

/** Whether an edited stamp clock report has a valid reported + break window. */
export const canSubmitStampReport = (
  inputs: StampReportTimeInputs,
): boolean => {
  const normalized = resolveStampReportTimes(inputs);
  return normalized != null && isReportedTimeWindowValid(normalized);
};

export interface ClockStampSubmitData {
  startTime: string;
  endTime: string;
  userNotes: string;
  breakTime?: string;
  breakStartTime?: string;
  breakEndTime?: string;
}

/**
 * Picks planned vs stamped time as the initial reported value: prefers planned
 * when the stamp is within the configured before/after thresholds, otherwise
 * uses the stamp.
 */
export const resolveReportedTimeSeed = (
  plannedIso: string,
  stampedIso: string | null | undefined,
  beforeThresholdMinutes: number,
  afterThresholdMinutes: number,
): string => {
  if (!stampedIso) return plannedIso;
  const minutes = signedMinutesBetweenIso(stampedIso, plannedIso);
  if (minutes == null) return stampedIso;
  if (minutes < 0 && Math.abs(minutes) <= beforeThresholdMinutes)
    return plannedIso;
  if (minutes > 0 && minutes <= afterThresholdMinutes) return plannedIso;
  if (minutes === 0) return plannedIso;
  return stampedIso;
};

/** The untouched reported and break inputs a stamp clock report starts out with. */
export const resolveDefaultStampTimeInputs = (
  timeReport: TimeReportDto,
  thresholds: ClockStampThresholds,
): StampReportTimeInputs => ({
  reportedStartBaseIso: timeReport.startTime,
  reportedEndBaseIso: timeReport.endTime,
  breakStartBaseIso: timeReport.breakStartTime ?? timeReport.startTime,
  breakEndBaseIso: timeReport.breakEndTime ?? timeReport.endTime,
  reportedStart: toTimeInputValue(
    resolveReportedTimeSeed(
      timeReport.startTime,
      timeReport.checkInTime,
      thresholds.beforeStartMinutes,
      thresholds.afterStartMinutes,
    ),
  ),
  reportedEnd: toTimeInputValue(
    resolveReportedTimeSeed(
      timeReport.endTime,
      timeReport.checkOutTime,
      thresholds.beforeEndMinutes,
      thresholds.afterEndMinutes,
    ),
  ),
  breakStart: toTimeInputValue(timeReport.breakStartTime),
  breakEnd: toTimeInputValue(timeReport.breakEndTime),
});

/**
 * Builds the stamp clock submit payload. When times are editable the break is
 * derived from the break inputs, otherwise the report's own break is kept.
 * Returns null when the inputs cannot be resolved to valid ISO times.
 */
export const buildStampSubmitData = (
  timeReport: TimeReportDto,
  canEditTime: boolean,
  inputs: StampReportTimeInputs,
  userNotes: string,
): ClockStampSubmitData | null => {
  if (!canEditTime) {
    const startTime = mergeIsoDateWithTimeInput(
      inputs.reportedStartBaseIso,
      inputs.reportedStart,
    );
    const endTime = mergeIsoDateWithTimeInput(
      inputs.reportedEndBaseIso,
      inputs.reportedEnd,
    );
    if (!startTime || !endTime) return null;
    return {
      startTime,
      endTime,
      userNotes,
      breakTime: timeReport.breakTime,
      breakStartTime: timeReport.breakStartTime,
      breakEndTime: timeReport.breakEndTime,
    };
  }

  const normalized = resolveStampReportTimes(inputs);
  if (!normalized) return null;
  return {
    startTime: normalized.startTime,
    endTime: normalized.endTime,
    userNotes,
    breakTime: String(
      minutesBetweenIso(normalized.breakStartTime, normalized.breakEndTime),
    ),
    breakStartTime: normalized.breakStartTime,
    breakEndTime: normalized.breakEndTime,
  };
};

/**
 * Builds the payload for submitting a stamp clock report without opening the
 * panel, using only the report's own default values. Returns null when those
 * defaults are incomplete or invalid, meaning the report needs manual editing.
 */
export const buildDefaultStampSubmitData = (
  timeReport: TimeReportDto,
  companySettings: Setting[] | undefined,
): ClockStampSubmitData | null => {
  const thresholds = resolveClockStampThresholds(companySettings);
  const inputs = resolveDefaultStampTimeInputs(timeReport, thresholds);
  // Without break times on the report there is nothing to normalize, so the
  // report's own break values are submitted as they are.
  const hasBreakInputs = inputs.breakStart !== "" && inputs.breakEnd !== "";
  const canEditTime = resolveCanEditTime(companySettings) && hasBreakInputs;

  if (canEditTime && !canSubmitStampReport(inputs)) return null;

  return buildStampSubmitData(timeReport, canEditTime, inputs, "");
};
